import { useState, useEffect } from 'react';
import { OrganizationProfile, OrganizationState, OrganizationUpdateData } from '../types';
import { ApiClient } from '@utils/apiClient';
import { API_ENDPOINTS } from '../constants';

export const useOrganization = () => {
  const [state, setState] = useState<OrganizationState>({
    profile: null,
    isLoading: true,
    isSaving: false,
    isEditing: false,
    error: null,
    fieldErrors: {},
  });

  // Load organization data on mount
  useEffect(() => {
    loadOrganization();
  }, []);

  const loadOrganization = async () => {
    setState(prev => ({ ...prev, isLoading: true, error: null }));

    try {
      const response = await ApiClient.get(API_ENDPOINTS.GET_CURRENT_ORGANIZATION);

      if (!response.ok) {
        throw new Error(`Failed to load organization: ${response.status} ${response.statusText}`);
      }

      const profile: OrganizationProfile = await response.json();

      setState(prev => ({
        ...prev,
        profile,
        isLoading: false,
      }));
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to load organization';
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: errorMessage,
      }));
    }
  };

  const updateProfile = (updates: Partial<OrganizationProfile>) => {
    if (!state.profile) return;

    // Clear field errors for updated fields
    const updatedFields = Object.keys(updates);
    const clearedFieldErrors = { ...state.fieldErrors };
    updatedFields.forEach(field => {
      delete clearedFieldErrors[field];
    });

    setState(prev => ({
      ...prev,
      profile: { ...prev.profile!, ...updates },
      fieldErrors: clearedFieldErrors,
    }));
  };

  const startEditing = () => {
    setState(prev => ({ ...prev, isEditing: true }));
  };

  const cancelEditing = () => {
    setState(prev => ({ ...prev, isEditing: false }));
  };

  const saveProfile = async (): Promise<{ success: boolean; error?: string }> => {
    if (!state.profile) {
      return { success: false, error: 'No organization data to save' };
    }

    setState(prev => ({ ...prev, isSaving: true, error: null, fieldErrors: {} }));

    try {
      // Prepare update data - only include fields that can be updated
      const updateData: OrganizationUpdateData = {
        name: state.profile.name,
        industry: state.profile.industry,
        size: state.profile.size,
        vat_id: state.profile.vat_id,
        tax_id: state.profile.tax_id,
        status: state.profile.status,
        contact_email: state.profile.contact_email,
        contact_phone: state.profile.contact_phone,
        address: state.profile.address,
        country: state.profile.country,
        billing_email: state.profile.billing_email,
        billing_address: state.profile.billing_address,
        description: state.profile.description,
        file: state.profile.file,
        timezone: state.profile.timezone,
      };

      const response = await ApiClient.patch(API_ENDPOINTS.UPDATE_ORGANIZATION, updateData);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));

        // Parse field_errors from backend response
        const fieldErrors: Record<string, string> = {};
        if (errorData.field_errors) {
          Object.entries(errorData.field_errors).forEach(([field, errors]) => {
            if (Array.isArray(errors) && errors.length > 0) {
              fieldErrors[field] = errors[0]; // Take first error message
            }
          });
        }

        // If there are field errors, set them and return
        if (Object.keys(fieldErrors).length > 0) {
          setState(prev => ({
            ...prev,
            isSaving: false,
            fieldErrors,
            error: null,
          }));
          return { success: false, error: 'Validation errors occurred' };
        }

        // Otherwise, use general error
        const errorMessage =
          errorData.detail ||
          `Failed to update organization: ${response.status} ${response.statusText}`;
        setState(prev => ({
          ...prev,
          isSaving: false,
          error: errorMessage,
        }));
        return { success: false, error: errorMessage };
      }

      const updatedProfile: OrganizationProfile = await response.json();

      setState(prev => ({
        ...prev,
        profile: updatedProfile,
        isSaving: false,
        isEditing: false,
        fieldErrors: {},
      }));

      return { success: true };
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to save organization profile';
      setState(prev => ({
        ...prev,
        isSaving: false,
        error: errorMessage,
        fieldErrors: {},
      }));
      return { success: false, error: errorMessage };
    }
  };

  const deleteOrganization = async (): Promise<{ success: boolean; error?: string }> => {
    setState(prev => ({ ...prev, error: null }));

    try {
      // IMPORTANT: ApiClient.delete() calls authService.getAuthHeaders() which reads
      // the Organization ID from localStorage. We must ensure it's available during the request.
      // The Organization ID is NOT cleared until AFTER this request completes successfully.
      const response = await ApiClient.delete(API_ENDPOINTS.DELETE_ORGANIZATION);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const errorMessage = errorData.detail || errorData.error || 'Failed to delete organization';
        return { success: false, error: errorMessage };
      }

      // Backend has already logged out the user and invalidated the session
      // Organization is marked as INACTIVE and deletion is in progress
      // Response contains: { message: string; deletion_in_progress: boolean }
      await response.json();
      return { success: true };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to delete organization';
      return { success: false, error: errorMessage };
    }
  };

  return {
    ...state,
    updateProfile,
    startEditing,
    cancelEditing,
    saveProfile,
    loadOrganization,
    deleteOrganization,
  };
};
